import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {ALL_NOTES} from "../utils/constants";

interface NavigationProps {
  selectedCategoryId: number;
}

export default function Navigation({ selectedCategoryId }: NavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileButtonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown and mobile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      // Only close mobile menu if clicking outside both the menu and the button
      if (mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        mobileButtonRef.current &&
        !mobileButtonRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    setDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const openMobileMenu = () => {
    setMobileMenuOpen(true);
    setDropdownOpen(false);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-md z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <div className="flex items-center">
            <Link to="/" className="max-md:hidden flex items-center">
              <img
                src="/logo512.png"
                alt="SnapNote Logo"
                className="h-8 w-8 object-contain"
              />
              <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">SnapNote</span>
            </Link>
            <Link to="/" className="md:hidden flex items-center">
              <img
                src="/snapnote-icon.png"
                alt="SnapNote Logo"
                className="h-16 w-16 object-contain"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="flex max-md:hidden md:items-center md:space-x-4">
            {isAuthenticated ? (
              <>
                <Link
                  to={`/notes?category=${ALL_NOTES.id}`}
                  className="text-gray-700 dark:text-gray-200 hover:text-teal-500 dark:hover:text-teal-400 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                >
                  All Notes
                </Link>
                <button
                  onClick={() => navigate('/notes/new', { state: { category_id: selectedCategoryId } })}
                  className="bg-brand-teal text-white hover:bg-brand-yellow hover:text-brand-navy px-4 py-2 rounded-xl transition-colors duration-200 font-semibold"
                >
                  New Note
                </button>
                <div className="relative" ref={dropdownRef}>
                  <button
                    className="flex items-center text-gray-700 dark:text-gray-200 hover:text-teal-500 dark:hover:text-teal-400 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    <span>{user?.username}</span>
                    <svg className="ml-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-gray-700 dark:text-gray-200 hover:text-teal-500 dark:hover:text-teal-400 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="bg-brand-teal text-white hover:bg-brand-yellow hover:text-brand-navy px-4 py-2 rounded-xl transition-colors duration-200 font-semibold"
                >
                  Sign up
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            ref={mobileButtonRef}
            type="button"
            className="flex md:hidden items-center justify-center p-2 rounded-md text-gray-700 dark:text-gray-200 hover:text-teal-500 dark:hover:text-teal-400 focus:outline-none"
            onClick={mobileMenuOpen ? closeMobileMenu : openMobileMenu}
            aria-expanded={mobileMenuOpen}
          >
            <span className="sr-only">{mobileMenuOpen ? 'Close menu' : 'Open menu'}</span>
            {mobileMenuOpen ? (
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        ref={mobileMenuRef}
        className={`${mobileMenuOpen ? 'block' : 'hidden'
          } md:hidden bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700`}
      >
        <div className="px-2 pt-2 pb-3 space-y-1">
          {isAuthenticated ? (
            <>
              <Link
                to="/notes"
                className="block text-gray-700 dark:text-gray-200 hover:text-teal-500 dark:hover:text-teal-400 px-3 py-2 rounded-md text-base font-medium"
                onClick={closeMobileMenu}
              >
                All Notes
              </Link>
              <button
                onClick={() => {
                  navigate('/notes/new');
                  closeMobileMenu();
                }}
                className="block w-full text-left bg-brand-teal text-white hover:bg-brand-yellow hover:text-brand-navy px-3 py-2 rounded-md text-base font-medium"
              >
                New Note
              </button>
              <button
                onClick={handleLogout}
                className="block w-full text-left text-gray-700 dark:text-gray-200 hover:text-teal-500 dark:hover:text-teal-400 px-3 py-2 rounded-md text-base font-medium"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="block text-gray-700 dark:text-gray-200 hover:text-teal-500 dark:hover:text-teal-400 px-3 py-2 rounded-md text-base font-medium"
                onClick={closeMobileMenu}
              >
                Sign in
              </Link>
              <Link
                to="/register"
                className="block bg-brand-teal text-white hover:bg-brand-yellow hover:text-brand-navy px-3 py-2 rounded-md text-base font-medium"
                onClick={closeMobileMenu}
              >
                Sign up
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
} 